import React from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';

const LAST_UPDATED = '19 de julho de 2026';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-white mb-3">{title}</h2>
    <div className="space-y-3 text-slate-300 text-sm leading-relaxed">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen login-bg p-4 md:p-10">
      <div className="grid-overlay" />

      <div className="max-w-3xl mx-auto relative z-10">
        <div className="flex justify-center mb-8">
          <Logo size="md" />
        </div>

        <Card className="glass-card border-white/10">
          <CardContent className="p-6 md:p-10">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-6 text-slate-400 hover:text-white -ml-2"
            >
              <ArrowLeft className="mr-2" size={16} />
              Voltar
            </Button>

            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Política de Privacidade
            </h1>
            <p className="text-slate-500 text-sm mb-8">Última atualização: {LAST_UPDATED}</p>

            <Section title="1. Quem somos">
              <p>
                Esta Política de Privacidade se aplica ao ELIOS, sistema de acompanhamento e
                coaching de performance do programa Elite, operado pela HUTOO EDUCAÇÃO
                ("nós", "nosso"). Ela descreve quais dados pessoais coletamos, como usamos,
                com quem compartilhamos e quais direitos você tem sobre eles, em conformidade
                com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 - LGPD).
              </p>
            </Section>

            <Section title="2. Quais dados coletamos">
              <p>Coletamos os seguintes dados quando você se cadastra e utiliza o ELIOS:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Dados de identificação: nome completo, e-mail, data de nascimento;</li>
                <li>Número de WhatsApp, para comunicação e identificação nas conversas;</li>
                <li>Foto de perfil (opcional);</li>
                <li>
                  Respostas do formulário de diagnóstico (os "11 Pilares" e metas pessoais e
                  profissionais), usadas para personalizar o acompanhamento;
                </li>
                <li>
                  Histórico de conversas trocadas com o assistente de IA ELIOS, seja pelo
                  aplicativo web ou pelo WhatsApp;
                </li>
                <li>
                  Dados de uso e respostas a pesquisas de satisfação (NPS) enviadas
                  periodicamente.
                </li>
              </ul>
            </Section>

            <Section title="3. Como usamos seus dados">
              <p>Usamos os dados coletados para:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Identificar você quando entra em contato pelo WhatsApp e personalizar as respostas do ELIOS;</li>
                <li>Gerar orientações, planos de ação e acompanhamento de metas com base no seu perfil;</li>
                <li>Enviar mensagens automáticas relacionadas ao programa (lembretes de metas, dicas práticas, felicitações de aniversário, pesquisas de satisfação);</li>
                <li>Melhorar a qualidade do atendimento e da plataforma;</li>
                <li>Cumprir obrigações legais e regulatórias, quando aplicável.</li>
              </ul>
            </Section>

            <Section title="4. Com quem compartilhamos seus dados">
              <p>
                Não vendemos seus dados pessoais. Compartilhamos dados apenas com prestadores
                de serviço estritamente necessários para o funcionamento do ELIOS, agindo como
                operadores nos termos da LGPD:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Meta / WhatsApp Business Platform</strong> — para envio e recebimento
                  das mensagens de WhatsApp;
                </li>
                <li>
                  <strong>Provedor de infraestrutura de IA</strong> — para processar suas
                  mensagens e gerar as respostas do assistente ELIOS;
                </li>
                <li>
                  <strong>Provedores de nuvem</strong> (hospedagem de banco de dados e
                  armazenamento de arquivos, incluindo fotos de perfil) — para manter a
                  plataforma em funcionamento de forma segura.
                </li>
              </ul>
            </Section>

            <Section title="5. Por quanto tempo guardamos seus dados">
              <p>
                Mantemos seus dados enquanto sua conta estiver ativa no programa Elite, ou
                pelo tempo necessário para cumprir obrigações legais. Ao encerrar sua
                participação no programa, você pode solicitar a exclusão dos seus dados,
                observados eventuais prazos de guarda exigidos por lei.
              </p>
            </Section>

            <Section title="6. Seus direitos">
              <p>De acordo com a LGPD, você tem direito a:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Confirmar a existência de tratamento e acessar seus dados;</li>
                <li>Corrigir dados incompletos, inexatos ou desatualizados;</li>
                <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários;</li>
                <li>Solicitar a portabilidade dos seus dados;</li>
                <li>Revogar o consentimento e se opor a tratamentos realizados com base nele;</li>
                <li>Solicitar informações sobre o compartilhamento dos seus dados.</li>
              </ul>
              <p>
                Para exercer qualquer um desses direitos, entre em contato pelo canal indicado
                na seção "Contato" abaixo.
              </p>
            </Section>

            <Section title="7. Segurança">
              <p>
                Adotamos medidas técnicas e administrativas razoáveis para proteger seus dados
                pessoais contra acessos não autorizados, perda, alteração ou vazamento, incluindo
                controle de acesso, criptografia de senhas e conexões seguras (HTTPS).
              </p>
            </Section>

            <Section title="8. Cookies e dados de navegação">
              <p>
                O ELIOS utiliza apenas os cookies/tokens estritamente necessários para manter
                sua sessão autenticada no aplicativo web. Não utilizamos cookies de publicidade
                ou rastreamento de terceiros.
              </p>
            </Section>

            <Section title="9. Alterações nesta política">
              <p>
                Esta política pode ser atualizada periodicamente. A data da última atualização
                está indicada no topo desta página. Alterações relevantes serão comunicadas aos
                usuários pelos canais habituais de contato.
              </p>
            </Section>

            <Section title="10. Contato">
              <p>
                Em caso de dúvidas sobre esta Política de Privacidade ou sobre o tratamento dos
                seus dados pessoais, entre em contato com a administração da HUTOO EDUCAÇÃO
                pelo e-mail{' '}
                <a href="mailto:admin@hutooeducacao.com" className="text-primary hover:underline">
                  admin@hutooeducacao.com
                </a>
                .
              </p>
            </Section>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-8">
          HUTOO EDUCAÇÃO © {new Date().getFullYear()} - Programa Elite
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
